import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { useUserDisplayInfo } from '@/context/UserDisplayContext';
import { useQueryClient } from '@tanstack/react-query';
import LanguageSwitcherBackoffice from '@/components/backoffice/LanguageSwitcherBackoffice';
import MobileBottomTabBar from './MobileBottomTabBar';
import MobileMoreDrawer from './MobileMoreDrawer';

interface MobileAppLayoutProps {
  children: React.ReactNode;
}

const PULL_THRESHOLD = 60;
const BACKGROUND_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

const MobileAppLayout = ({ children }: MobileAppLayoutProps) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { t } = useLanguage();
  const { displayInfo, loading } = useUserDisplayInfo();
  const queryClient = useQueryClient();

  const touchStartY = useRef(0);
  const isPulling = useRef(false);
  const mainRef = useRef<HTMLElement>(null);
  const backgroundTimestamp = useRef<number>(0);

  const userName = loading ? '...' : displayInfo?.first_name || displayInfo?.username || 'User';

  // Pull-to-refresh handlers
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries();
    // Brief visual feedback
    setTimeout(() => {
      setIsRefreshing(false);
      setPullDistance(0);
    }, 600);
  }, [queryClient]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const scrollTop = mainRef.current?.scrollTop ?? 0;
    if (scrollTop <= 0 && !isRefreshing) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, [isRefreshing]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling.current || isRefreshing) return;
    const deltaY = e.touches[0].clientY - touchStartY.current;
    if (deltaY > 0) {
      // Apply resistance — pull distance grows slower than finger movement
      setPullDistance(Math.min(deltaY * 0.4, 100));
    } else {
      isPulling.current = false;
      setPullDistance(0);
    }
  }, [isRefreshing]);

  const onTouchEnd = useCallback(() => {
    if (!isPulling.current) return;
    isPulling.current = false;
    if (pullDistance >= PULL_THRESHOLD) {
      handleRefresh();
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, handleRefresh]);

  // Capacitor status bar config
  useEffect(() => {
    const configureStatusBar = async () => {
      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#111827' });
        await StatusBar.setOverlaysWebView({ overlay: true });
      } catch (e) {
        // Not running on native — ignore
      }
    };
    configureStatusBar();
  }, []);

  // App resume listener — invalidate cache after 2+ min in background
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const setupAppListener = async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) {
            backgroundTimestamp.current = Date.now();
          } else {
            const elapsed = Date.now() - backgroundTimestamp.current;
            if (backgroundTimestamp.current > 0 && elapsed >= BACKGROUND_THRESHOLD_MS) {
              queryClient.invalidateQueries();
            }
          }
        });
        cleanup = () => handle.remove();
      } catch (e) {
        // Not running on native — ignore
      }
    };

    setupAppListener();
    return () => cleanup?.();
  }, [queryClient]);

  const indicatorOpacity = Math.min(pullDistance / PULL_THRESHOLD, 1);
  const indicatorScale = 0.5 + indicatorOpacity * 0.5;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Compact top header */}
      <header className="sticky top-0 z-40 flex items-center justify-between px-4 h-20 bg-gray-900 border-b border-gray-700 shadow-sm safe-area-top">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/logo_1_small_inverted_no_background.png" alt="TheClub" className="h-8 w-auto flex-shrink-0" />
          <span className="text-base font-semibold text-white truncate max-w-[140px]">
            {userName}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full text-white hover:bg-gray-800"
            aria-label={t('backoffice.navbar.notifications')}
          >
            <Bell className="h-4 w-4" />
          </Button>
          <LanguageSwitcherBackoffice buttonClassName="h-8 w-8 rounded-full text-white hover:bg-gray-800" />
        </div>
      </header>

      {/* Pull-to-refresh indicator */}
      <div
        className="flex items-center justify-center overflow-hidden bg-muted transition-all"
        style={{ height: isRefreshing ? 40 : pullDistance > 0 ? pullDistance * 0.4 : 0 }}
      >
        <RefreshCw
          className={`h-5 w-5 text-muted-foreground transition-transform ${isRefreshing ? 'animate-spin' : ''}`}
          style={{
            opacity: isRefreshing ? 1 : indicatorOpacity,
            transform: `scale(${isRefreshing ? 1 : indicatorScale}) rotate(${pullDistance * 2}deg)`,
          }}
        />
      </div>

      {/* Page content — add bottom padding to clear the tab bar */}
      <main
        ref={mainRef}
        className="flex-1 bg-muted pb-20 overflow-y-auto"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {children}
      </main>

      {/* Bottom tab bar */}
      <MobileBottomTabBar onMorePress={() => setMoreOpen(true)} />

      {/* More drawer */}
      <MobileMoreDrawer open={moreOpen} onOpenChange={setMoreOpen} />
    </div>
  );
};

export default MobileAppLayout;
